<template>
  <div id="CampaignEditSegmentationEdit">
    <ValidationObserver v-slot="{ invalid }" slim>
      <Page
        v-if="loading"
        :title="$t('Editing Segment')"
        :breadcrumbs="[
          {
            label: $t('Back to list'),
            onAction: () => cancel()
          }
        ]"
      >
        <campaign-edit-header :campaign="campaign"></campaign-edit-header>
        <LoadingLayout />
      </Page>
      <Page
        v-else
        :title="`${$t('Editing Segment')}: '${segment.name}'`"
        :primaryAction="{
            label: $t('Save'),
            onAction: () => saveSegment(),
            disabled: invalid || cannotSave || saving
          }"
        :breadcrumbs="hasChanged ? [
          {
            label: $t('Cancel'),
            onAction:() => cancel()
          }
        ] : [
          {
            label: $t('Back to list'),
            onAction: () => cancel()
          }
        ]"
      >
        <campaign-edit-header :campaign="campaign"></campaign-edit-header>
        <Layout>
          <LayoutSection>
            <Card :title="segment.status === 'active' ? `${$t('Segment')} (${$t('Enabled')})` : `${$t('Segment')} (${$t('Disabled')})`">
              <CardSection>
                <FormLayout>
                  <FormLayoutGroup>
                    <validation-provider :name="$t('Name')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Name')"
                        v-model="segment.name"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('Tag')" immediate rules="tagRegex" v-slot="{ errors }">
                      <TextField
                        :label="$t('Tag')"
                        v-model="segment.tag"
                        :help-text-html="$t('campaignEdit.segment.tagHelp')"
                        :error="errors[0]"
                      />
                    </validation-provider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
              <CardSection>
                <FormLayout>
                  <Select
                    :label="$t('Type')"
                    v-model="segment.type"
                    :options="[
                      {
                        label: $t('campaignEdit.segment.type.standard'),
                        value: 'include'
                      },
                      {
                        label: $t('campaignEdit.segment.type.exitIntent'),
                        value: 'exit-intent'
                      },
                      {
                        label: $t('campaignEdit.segment.type.hideOffers'),
                        value: 'display-none'
                      },
                      {
                        label: $t('campaignEdit.segment.type.doNotTarget'),
                        value: 'exclude'
                      },
                      {
                        label: $t('campaignEdit.segment.type.ignoreUser'),
                        value: 'ignore-user'
                      }
                    ]"
                    :help-text="typeHelpText"
                    :error="exitIntentOfferError || exitIntentWidgetError"
                  />
                  <TextField
                    v-if="segment.type === 'ignore-user'"
                    :label="$t('Days (24 hours) to ignore users after initially falling into segment')"
                    type="number"
                    v-model="segment.ignoreUserDays"
                    :error="ignoreUserDaysError"
                  />
                  <Checkbox
                    v-if="segment.type === 'include' || segment.type === 'exit-intent'"
                    v-model="segment.overrideGenius"
                    :label="$t('Override Algorithms')"
                    :help-text="$t('campaignEdit.segment.overrideHelp')"
                    :error="geniusUpsellError"
                  />
                  <Checkbox
                    v-if="segment.type === 'include'"
                    v-model="segment.isUpsell"
                    :label="$t('Upsell Segment')"
                    :help-text="$t('campaignEdit.segment.upsellHelp')"
                    :error="geniusUpsellError || upsellOfferError"
                  />
                  <Checkbox
                    v-if="segment.type === 'exclude'"
                    v-model="segment.sessionStickiness"
                    :label="$t('Sticky Segment')"
                    :help-text="$t('campaignEdit.segment.stickyHelp')"
                  />
                  <Checkbox
                    v-if="segment.type === 'include' || segment.type === 'exit-intent' || segment.type === 'ignore-user'"
                    v-model="segment.overrideDnt"
                    :label="$t('Override Do Not Target Segments')"
                    :help-text="$t('campaignEdit.segment.overrideDntHelp')"
                  />
                </FormLayout>
              </CardSection>
            </Card>
            <div style="padding-top: 20px; padding-bottom: 20px;">
              <ruleset-editor
                :locale="theUser.defaultLanguage"
                mode="normal"
                :auth-key="authKey"
                :account-rulesets="accountRulesets"
                :rulesets="sourceRulesets"
                :history="[]"
                :can-revoke="canRevokeOffers"
                :campaign-id="campaign.id"
                :whitelisted-rule-types="whiteListedRuleTypes"
                @update="val => rulesetState = val"
              ></ruleset-editor>
            </div>
            <SegmentOfferManager
              :campaign="campaign"
              :offersVisible="offersVisible"
              :offers="offers"
              :segment="segment"
            />
            <Card v-if="offersVisible" :title="$t('Widgets')">
              <CardSection v-if="widgets.length">
                <Select
                  v-model="segment.widgetId"
                  :options="formattedWidgets"
                  :placeholder="$t('campaignEdit.segment.Select a widget')"
                  :help-text="$t('campaignEdit.segment.widgetHelp')"
                  :error="!segment.widgetId ? $t('campaignEdit.error.setWidget') : null"
                />
              </CardSection>
              <CardSection v-else>
                <a>{{$t('campaignEdit.segment.noWidgetMessage')}}</a>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>
        <PageActions
          v-if="!loading"
          :primaryAction="{
            label: $t('Save'),
            onAction: () => saveSegment(),
            disabled: invalid || cannotSave || saving
          }"
          :secondaryActions="hasChanged ? [
            {
              label: $t('Cancel'),
              type: 'destructive',
              onAction:() => cancel()
            }
          ] : [
            {
              label: $t('Back to list'),
              onAction: () => cancel()
            }
          ]"
        />
      </Page>
    </ValidationObserver>
  </div>
</template>

<script>
import _ from 'lodash'
import OffersUtil from '../classes/utils/OffersUtil'
import CampaignEditHeader from '@/components/CampaignEditHeader'
import SegmentOfferManager from '@/components/SegmentOfferManager'
import { extend as ValidateExtend } from 'vee-validate/dist/vee-validate.full'

const RulesetEditor =  () => {
  return {
    component: window.RulesetEditorLoader
      .load()
      .then(mod => mod.Editor),
    loading: {
      render(h) {
        return h('div', `${this.$t('Loading')} ...`)
      }
    }
  }
}

export default {
  name: 'CampaignEditSegmentationEdit',
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      saving: false,
      segment: null,
      copySegment: null,
      accountRulesets: [],
      widgets: [],
      offers: [],
      accountPrefs: null,
      authKey: null,
      rulesetState: null,
      whiteListedRuleTypes: [],
      selectedId: null,
      sortBy: {
        column: 'displayText',
        direction: 'asc'
      }
    }
  },
  mounted() {

    this.eventHub.$on('tokenRefreshed', () => {
      this.api.getSessionKey()
      .then((sessionKey) => {
        this.authKey = sessionKey
      })
      .catch(this.apiCatch)
    })

    ValidateExtend('tagRegex', (value) => {
      if (value && !value.match(/^[a-zA-Z0-9_-]{3,25}$/)) {
        return this.$t('campaignEdit.segment.tagRegexValidate')
      }
      return true
    })

    NProgress.start()
    Promise.all([
      this.api.getSegment(this.$route.params.segmentId), 
      this.api.getAccountRulesetsForSegmentation(), 
      this.api.getCurrentAccountPrefs(),
      this.api.getIncentives(this.campaign.id),
      this.api.getWidgetsForSegmentation(),
      this.api.getSessionKey()
    ])
      .then((data) => {
        const [
          segment,
          accountRulesets,
          accountPrefs,
          offers,
          widgets,
          sessionKey
        ] = data
        this.segment = segment
        this.accountRulesets = accountRulesets
        this.accountPrefs = accountPrefs
        this.offers = OffersUtil.fillForFrontend(offers)
        // console.log(data)
        this.setActiveIds()
        this.widgets = widgets
        this.authKey = sessionKey
        this.copySegment = _.cloneDeep(this.segment)
        try {
          this.whiteListedRuleTypes = JSON.parse(accountPrefs.segmentationRuleWhitelist || '[]') || []
        } catch(e) { console.log(e) }
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    typeHelpText() {
      if (this.segment.type === 'include') {
        return this.$t('campaignEdit.segment.widgetTypeIncludeHelp')
      }
      if (this.segment.type === 'exit-intent') {
        return this.$t('campaignEdit.segment.widgetTypeExitIntentHelp')
      }
      if (this.segment.type === 'exclude') {
        return this.$t('campaignEdit.segment.widgetTypeExcludeHelp')
      }
      if (this.segment.type === 'display-none') {
        return this.$t('campaignEdit.segment.widgetTypeDisplayNoneHelp')
      }
      if (this.segment.type === 'ignore-user') {
        return this.$t('campaignEdit.segment.widgetTypeIgnoreUserHelp')
      }
      return ''
    },
    formattedWidgets() {
      const w = this.widgets.filter(w => {
        return w.type !== 'Smart'
      })
      .map(w => {
        return {
          label: w.name,
          value: w.id
        }
      })
      w.unshift({
        label: `- ${this.$t('Use Default')} -`,
        value: -1
      })
      return w
    },
    sourceRulesets() {
      return _.cloneDeep(this.segment.rulesets)
    },
    canRevokeOffers() {
      return this.accountPrefs.canRevokeOffers
    },
    offersVisible() {
      // return true
      return this.segment.type !== 'exclude' &&
        this.segment.type !== 'display-none' &&
        this.segment.type !== 'ignore-user'
    },
    geniusUpsellError() {
      if (this.segment && this.segment.type === 'include') {
        if (this.segment.isUpsell && this.segment.overrideGenius) {
          return this.$t('campaignEdit.segment.overrideUpsellError')
        }
      }
      return false
    },
    upsellOfferError() {
      if (this.segment && this.segment.type === 'include') {
        if (this.segment.isUpsell && this.segment.offerIds.length < 1) {
          return this.$t('campaignEdit.segment.upsellOfferError')
        }
      }
      return false
    },
    exitIntentOfferError() {
      if (this.segment) {
        if (this.segment.type === 'exit-intent' && this.segment.offerIds.length < 1) {
          return this.$t('campaignEdit.segment.exitIntentOfferError')
        }
      }
      return false
    },
    exitIntentWidgetError() {
      if (this.segment) {
        if (this.segment.type === 'exit-intent' && this.widgets && this.widgets.length && this.segment.widgetId <= 0) {
          return this.$t('campaignEdit.segment.exitIntentWidgetError')
        }
      }
      return false
    },
    ignoreUserDaysError() {
      if (this.segment && this.segment.type === 'ignore-user') {
        if (this.segment.ignoreUserDays < 1) {
          return this.$t('campaignEdit.segment.ignoreUserDaysError')
        }
      }
      return false
    },
    cannotSave() {
      if (this.offersVisible && this.widgets && this.widgets.length && !this.segment.widgetId) {
        return true
      }
      if (this.ignoreUserDaysError ||
        this.exitIntentWidgetError ||
        this.exitIntentOfferError ||
        this.upsellOfferError ||
        this.geniusUpsellError) {
          return true
        }
      if (!this.rulesetState || (this.rulesetState && this.rulesetState.hasError)) return true
      return false
    },
    hasChanged() {
      return !_.isEqual(this.segment, this.copySegment)
    } 
  },
  methods: {
    setActiveIds() {
      if (this.segment) {
        this.offers.forEach(o => {
          o.isActive = false
        })
        for (const offerId of this.segment.offerIds) {
          if (offerId > 0) {
            let offerIndex = this.offers.findIndex(o => o.id === offerId)
            if (this.offers[offerIndex]) {
              this.offers[offerIndex].isActive = true
            }
          }
        }
      }
    },
    async cancel() {
      // console.log(_.isEqual(this.segment, this.copySegment))
      if (this.hasChanged) {
        const confirm = await this.$ui.confirm({
          title: this.$t('Unsaved Changes'),
          content: this.$t('campaignEdit.segment.unsavedChangesMessage'),
          primaryActionLabel: this.$t('Lose changes and return to list'),
          primaryActionType: 'destructive',
          secondaryActionLabel: this.$t('Continue editing')
        })
        if (!confirm) {
          return
        }
      }
      this.$router.push('/campaigns/' + this.campaign.id + '/segmentation')
    },
    saveSegment() {
      if (!this.offersVisible) {
        this.segment.offerIds = []
      }
      const saveSegment = {
        ...this.segment,
        rulesets: this.rulesetState.rulesets
      }
      NProgress.start()
      this.saving = true
      this.api.updateSegment(saveSegment)
        .then((seg) => {
          this.segment = seg
          this.setActiveIds()
          this.copySegment = _.cloneDeep(this.segment)
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
          this.$ui.toast({ title: this.$t('Saved') })
        })
    }
  },
  components: {
    RulesetEditor,
    CampaignEditHeader,
    SegmentOfferManager
  }
}

</script>

<style lang="scss">
// #CampaignEditSegmentationEdit {
// }
</style>
